// 与后端 domain 对齐的 TS 类型（V1 通用版）。
// 若要 100% 同步，可用 openapi-typescript 从 /openapi.json 自动生成。

// -------------------- 通用 --------------------

export type UUID = string
export type ISODateString = string

export type JobState =
  | 'created'
  | 'queued'
  | 'processing'
  | 'wait_review'
  | 'success'
  | 'partial_success'
  | 'failed'
  | 'cancelled'

export type SpaceType =
  | 'sales'
  | 'kitchen'
  | 'restroom'
  | 'storage'
  | 'office'
  | 'corridor'
  | 'other'

export const SPACE_TYPE_LABELS: Record<SpaceType, string> = {
  sales: '营业区',
  kitchen: '厨房',
  restroom: '卫生间',
  storage: '仓库',
  office: '办公',
  corridor: '走廊',
  other: '其他',
}

export const FINISH_SURFACE_LABELS: Record<'floor' | 'wall' | 'ceiling', string> = {
  floor: '地面',
  wall: '墙面',
  ceiling: '天花',
}

/** 套房间类型的工序，不在工程量预览 / 报告主表展示 */
export const HIDDEN_TEMPLATE_CODES = new Set([
  'FLOOR.LEVEL',
  'WALL.LEVEL',
  'WALL.PUTTY',
  'WALL.PAINT',
  'CEIL.LEVEL',
  'CEIL.PUTTY',
  'CEIL.PAINT',
  'FLOOR.WATERPROOF',
  'WALL.WATERPROOF_KITCHEN',
  'WALL.WATERPROOF_RESTROOM',
])

export const JOB_STATE_LABELS: Record<JobState, string> = {
  created: '已创建',
  queued: '排队中',
  processing: '处理中',
  wait_review: '待复核',
  success: '成功',
  // 前端暂时不单独展示「部分成功」，与成功同等对待
  partial_success: '成功',
  failed: '失败',
  cancelled: '已停止',
}

export const JOB_STATE_COLORS: Record<JobState, string> = {
  created: 'default',
  queued: 'blue',
  processing: 'blue',
  wait_review: 'purple',
  success: 'green',
  partial_success: 'green',
  failed: 'red',
  cancelled: 'default',
}

const ACTIVE_JOB_STATES: JobState[] = ['created', 'queued', 'processing']
const IN_FLIGHT_JOB_STATES: JobState[] = [
  'created',
  'queued',
  'processing',
  'wait_review',
]

export function isJobActive(state: JobState): boolean {
  return ACTIVE_JOB_STATES.includes(state)
}

/** 未结束（含待复核）：同一项目同时只允许一个，要重跑必须先停。 */
export function isJobInFlight(state: JobState): boolean {
  return IN_FLIGHT_JOB_STATES.includes(state)
}

export function jobActionLabel(state: JobState): string {
  if (state === 'wait_review') return '去复核'
  if (state === 'success' || state === 'partial_success') return '查看结果'
  if (state === 'failed') return '查看原因'
  return '查看进度'
}

export const WORKFLOW_STEP_LABELS: Record<string, string> = {
  drawing_classify: '图纸分类',
  cad_parse: 'CAD 解析',
  cad_quality_check: '质量检查',
  layer_classify: '图层分类',
  plan_index_extract: '索引提取',
  space_detect: '空间识别',
  plan_target_binding: '平立面绑定',
  ai_assist: 'AI 辅助',
  process_note_extract: '工艺注记',
  fixture_legend_extract: '灯具图例',
  fixture_count: '灯具计数',
  door_schedule_extract: '门表明细',
  door_count: '门樘计数',
  new_wall_extract: '新砌墙',
  material_catalog_extract: '材料表',
  elevation_finish_extract: '立面墙材',
  wait_review: '待复核',
  quantity_calculate: '工程量计算',
  validate: '校验',
  report_generate: '生成报告',
  done: '完成',
}

// -------------------- Project --------------------

export interface Project {
  id: UUID
  name: string
  status: string
  brand_id: string
  template_version: string
  template_hash: string
  param_overrides: Record<string, number | string>
  pid?: number | null
  shop_type_id?: number | null
  chain_brand_id?: number | null
  inventory_mould_id?: number | null
  inventory_id?: number | null
}

export interface CreateProjectRequest {
  name: string
  param_overrides?: Record<string, number | string>
  pid?: number | null
  shop_type_id?: number | null
  chain_brand_id?: number | null
  inventory_mould_id?: number | null
  inventory_id?: number | null
}

export interface ListProjectsQuery {
  id?: string
  pid?: number
  shop_type_id?: number
  chain_brand_id?: number
  inventory_mould_id?: number
  inventory_id?: number
}

// -------------------- CadFile --------------------

export interface CadFile {
  id: UUID
  filename: string
  format: string
  sha256: string
  size_bytes: number
}

// -------------------- Job --------------------

export interface Job {
  id: UUID
  project_id: UUID
  state: JobState
  current_skill: string | null
  progress: number // 0-100
  error_code: string | null
  error_message: string | null
  started_at: ISODateString | null
  finished_at: ISODateString | null
  created_at?: ISODateString | null
}

export interface CreateJobRequest {
  file_ids: UUID[]
}

// -------------------- Space --------------------

export interface SpaceReviewItem {
  id: UUID
  space_key: string
  name: string
  space_type: SpaceType | string
  area_m2: number
  perimeter_m: number
  is_verified: boolean
  is_deleted: boolean
  param_overrides: Record<string, number>
  version: number
  confidence: number
  parent_id?: UUID | null
  name_source?: string | null
}

export const NAME_SOURCE_LABELS: Record<string, string> = {
  text_in_poly: '图纸文字',
  rule: '规则',
  ai: 'AI',
  manual: '人工',
  unknown: '未命名',
}

export interface UpdateSpaceRequest {
  version: number
  name?: string
  space_type?: SpaceType
  is_deleted?: boolean
  param_overrides?: Record<string, number>
}

export interface ReviewMapDrawing {
  filename: string | null
  drawing_type: string | null
  role: string | null
  sheet_nos: string[]
  sheet_title: string | null
  layout_name: string | null
  source_label: string | null
}

export interface ReviewMapSpace {
  space_key: string
  name: string
  space_type: string
  area_m2: number
  is_deleted: boolean
  ring: number[][]
  centroid: [number, number]
}

export interface ReviewMapBBox {
  min_x: number
  min_y: number
  max_x: number
  max_y: number
}

export interface ReviewMap {
  drawing: ReviewMapDrawing | null
  bbox: ReviewMapBBox | null
  spaces: ReviewMapSpace[]
  has_overlay: boolean
  has_preview: boolean
}

/** POST /jobs/{id}/shopfront 返回。 */
export interface ShopfrontApplyResponse {
  kept: number
  dropped: number
  outline_area_m2: number
  outline_perimeter_m: number
}

export const SPACE_TYPE_COLORS: Record<string, string> = {
  sales: '#1677ff',
  kitchen: '#fa8c16',
  restroom: '#13c2c2',
  storage: '#722ed1',
  office: '#52c41a',
  corridor: '#8c8c8c',
  other: '#d4b106',
}

// -------------------- SurfaceRegion（P4b / 复核面层控制台）--------------------

export type MaterialSource = 'legend' | 'rule' | 'manual'

export const MATERIAL_SOURCE_LABELS: Record<MaterialSource, string> = {
  legend: '图例',
  rule: '规则',
  manual: '手工',
}

export const SURFACE_REGION_PALETTE = [
  '#1677ff',
  '#fa541c',
  '#52c41a',
  '#722ed1',
  '#13c2c2',
  '#eb2f96',
  '#faad14',
  '#2f54eb',
  '#a0d911',
  '#fa8c16',
  '#1890ff',
  '#c41d7f',
]

export function surfaceRegionColor(id: string): string {
  let h = 0
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0
  return SURFACE_REGION_PALETTE[h % SURFACE_REGION_PALETTE.length]
}

export interface SurfaceRegionItem {
  id: string
  space_id: string | null
  layout_name: string | null
  pattern_signature: string
  pattern_name: string
  layer_name: string | null
  color: number | null
  area_m2: number
  hatch_count: number
  material_name: string | null
  material_text: string | null
  material_code: string | null
  material_source: MaterialSource | string | null
  confidence: number
  ring: number[][]
}

export interface UpdateSurfaceRegionRequest {
  material_name: string
}

// -------------------- Quantity --------------------

export interface QuantityItem {
  space_id: string
  category: string
  sub_category: string
  code: string
  name: string
  quantity: number
  unit: string
  formula: string
  source_type: 'cad_geometry' | 'manual' | 'equipment_catalog'
  // V1.5：图纸溯源
  plan_sheet_no?: string | null
  elevation_sheet_nos?: string[]
}

export const SOURCE_TYPE_LABELS: Record<QuantityItem['source_type'] | 'sum', string> = {
  cad_geometry: 'CAD几何',
  manual: '人工填',
  equipment_catalog: '设备表',
  sum: '合计',
}

// -------------------- Report --------------------

export interface ReportMeta {
  job_id: UUID
  generated_at: ISODateString | null
  has_json: boolean
  has_xlsx: boolean
  has_pdf: boolean
  download_urls: {
    json: string
    xlsx: string
    pdf: string
  }
}

export type FinishSurface = 'floor' | 'wall' | 'ceiling'

export type FinishSource = 'hatch' | 'manual' | 'unnamed' | 'process_note' | 'callout'

export interface FinishLine {
  drawing_name: string
  material_code: string | null
  surface: FinishSurface
  area_m2: number
  source: FinishSource
  entity_ids?: string[]
  remarks?: string[]
  qty_unit?: string | null
  sheet_nos?: string[]
}

export interface FixtureLine {
  code: string
  drawing_name: string
  count: number
  space_id?: string | null
  entity_ids?: string[]
  remarks?: string[]
  sheet_nos?: string[]
  is_install?: boolean
}

export interface OpeningLine {
  kind: 'door' | 'window' | string
  drawing_name: string
  count: number
  width_m: number
  height_m?: number | null
  space_id?: string | null
  opening_ids?: string[]
  code?: string
  remarks?: string[]
  unit?: string
  sheet_nos?: string[]
  is_install?: boolean
}

export interface EquipmentLine {
  drawing_name: string
  code?: string
  kind: 'furniture' | 'appliance' | 'water_sign' | string
  count: number
  unit?: string
  space_id?: string | null
  entity_ids?: string[]
  remarks?: string[]
  sheet_nos?: string[]
}

export interface RoomNode {
  space_id: string
  name: string
  area_m2: number
  ceiling_m2?: number | null
  children: RoomNode[]
  finishes: FinishLine[]
  fixtures?: FixtureLine[]
  openings?: OpeningLine[]
  equipment?: EquipmentLine[]
  warnings?: string[]
}

export interface SpaceBill {
  shop_name: string
  shop_area_m2: number
  rooms: RoomNode[]
  unbound_fixtures?: FixtureLine[]
  unbound_finishes?: FinishLine[]
  unbound_equipment?: EquipmentLine[]
  unbound_openings?: OpeningLine[]
}

// -------------------- V1.5：平面索引与图纸绑定 --------------------

export type IndexKind = 'elevation' | 'detail' | 'section' | 'opening' | 'equipment'

export const INDEX_KIND_LABELS: Record<IndexKind, string> = {
  elevation: '立面',
  detail: '大样/详图',
  section: '剖面',
  opening: '门窗',
  equipment: '设备/家具',
}

export interface PlanIndexRef {
  id: UUID
  kind: IndexKind
  view_no: string
  sheet_no: string | null
  anchor_xy: [number, number]
  direction?: [number, number] | null
  extent?: Record<string, unknown> | null
  detected_by: string
  confidence: number
  source_entity_ids: string[]
  attached_space_key: string | null
  raw_text: string | null
}

export interface PlanIndexRefSet {
  refs: PlanIndexRef[]
  warnings: Array<Record<string, unknown>>
  counts_by_kind: Record<string, number>
}

export interface PlanTargetBinding {
  ref_id: UUID
  kind: IndexKind
  view_no: string
  sheet_no: string | null
  matched: boolean
  reason: string
  target_file_id: UUID | null
  target_filename: string | null
  target_drawing_type: string | null
  anchor_space_key: string | null
  // V1.5 Phase 1.2：门/窗归属
  anchor_opening_id?: string | null
}

export interface PlanTargetBindingSet {
  bindings: PlanTargetBinding[]
  warnings: Array<Record<string, unknown>>
  total_refs: number
  matched: number
  unmatched: number
  orphan_anchors: number
  // V1.5 Phase 1.2：门/窗 / 空间 → 立面图号
  opening_elevation_map?: Record<string, string[]>
  space_elevation_map?: Record<string, string[]>
}

export interface Drawing {
  id: UUID
  file_id: UUID
  drawing_type: string
  role: string
  preview_key: string | null
  classify_confidence: number
  detected_by: string
  sheet_nos: string[]
  sheet_title: string | null
  filename: string | null
  // V1.5 Phase 2：Paperspace layout 拆分。老数据 layout_source='model', layout_name=null
  layout_name?: string | null
  layout_source?: 'model' | 'paperspace'
}

export interface DrawingSet {
  primary_plan_file_id: UUID
  // V1.5 Phase 2：指向按 layout 拆分后的主平面 Drawing.id；老数据为 null
  primary_plan_drawing_id?: UUID | null
  drawings: Drawing[]
}

export interface UpsertPlanIndexRefRequest {
  kind: IndexKind
  view_no: string
  sheet_no?: string | null
  anchor_xy: [number, number]
  ref_id?: UUID
}

// -------------------- API 错误 --------------------

export interface ApiError {
  code: string
  message: string
  details?: Record<string, unknown>
}

export type SheetPngRunStatus = 'pending' | 'partial' | 'ready' | 'failed'

export interface SheetPreviewItem {
  sheet_no: string
  status: 'pending' | 'partial' | 'ready' | 'failed' | 'missing' | string
  url?: string | null
  sha256?: string | null
  error_message?: string | null
}

export interface SheetPngStatus {
  status: SheetPngRunStatus | string
  done: number
  total: number
  message: string
  warnings: string[]
  items: SheetPreviewItem[]
}
